import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Edit2, Trash2, CalendarRange, Gauge } from 'lucide-react';
import Shell from '../components/TopBarShell';
import MonthGrid from '../components/calendar/MonthGrid';
import EventComposer from '../components/calendar/EventComposer';
import PressureGrid from '../components/calendar/PressureGrid';
import GoogleCalendarConnect from '../components/calendar/GoogleCalendarConnect';
import EmptyState from '../components/EmptyState';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthProvider';
import { useLocale, useT } from '../contexts/LocaleProvider';
import { useReviewQueue } from '../contexts/ReviewQueueContext';
import { eventLabel } from '../lib/holidayLabels';
import { deptLabel } from '../lib/departments';
import { buildDisplayNameFor } from '../lib/eventNames';
import api from '../utils/api';

const KIND_KEYS = ['public_holiday', 'closure', 'team_event', 'absence'];

const KIND_DOT = {
  public_holiday: 'hsl(0 60% 60%)',
  closure:        'hsl(30 70% 55%)',
  team_event:     'hsl(150 35% 45%)',
  absence:        'hsl(220 25% 50%)',
};

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Calendar = () => {
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();
  const canEditEvent = (e) => isHR || (
    e?.kind === 'absence' && (e?.employee_email || '').toLowerCase() === myEmail
  );
  const { count: reviewCount } = useReviewQueue();
  const { locale, intlLocale } = useLocale();
  const t = useT();
  const ALL = t('common.all');

  const [now] = useState(() => new Date());
  const [year, setYear]       = useState(now.getFullYear());
  const [monthIdx, setMonth]  = useState(now.getMonth());
  const [events, setEvents]   = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isHR, setIsHR]       = useState(false);
  const [activeDept, setActiveDept] = useState(ALL);
  const [composing, setComposing]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [view, setView]             = useState('month');  // 'month' | 'pressure'

  // Window: include leading/trailing days of the month grid so events spanning
  // into adjacent months show up as expected.
  const windowRange = useMemo(() => {
    const start = new Date(year, monthIdx, 1);
    start.setDate(start.getDate() - 7);  // safe buffer
    const end = new Date(year, monthIdx + 1, 0);
    end.setDate(end.getDate() + 14);
    return { start: isoDate(start), end: isoDate(end) };
  }, [year, monthIdx]);

  useEffect(() => {
    api.events(windowRange).then(setEvents).catch(() => setEvents([]));
  }, [windowRange]);

  useEffect(() => {
    api.employees().then(setEmployees).catch(() => setEmployees([]));
    api.isHR().then(setIsHR).catch(() => setIsHR(false));
  }, []);

  const departments = useMemo(() => {
    if (!events) return [];
    const set = new Set([ALL]);
    for (const e of employees) if (e.departement) set.add(e.departement);
    return [...set];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, employees]);

  const empDeptByEmail = useMemo(() => {
    const m = new Map();
    for (const e of employees) {
      if (e.email) m.set(e.email.toLowerCase(), e.departement || '');
    }
    return m;
  }, [employees]);

  const filtered = useMemo(() => {
    if (!events) return [];
    if (activeDept === ALL) return events;
    return events.filter(e => {
      if (e.departement) return e.departement === activeDept;
      // No explicit dept on the event. If it's tied to an employee, fall
      // back to that employee's current department · keeps old absences
      // (created before the backend snapshotted dept) from leaking across
      // teams. Events with no dept AND no employee are global (holidays,
      // company-wide closures) and stay visible on every filter.
      if (e.employee_email) {
        const dept = empDeptByEmail.get(e.employee_email.toLowerCase());
        return dept ? dept === activeDept : false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, activeDept, empDeptByEmail]);

  const goPrev  = () => {
    let m = monthIdx - 1, y = year;
    if (m < 0) { m = 11; y -= 1; }
    setMonth(m); setYear(y);
  };
  const goNext  = () => {
    let m = monthIdx + 1, y = year;
    if (m > 11) { m = 0; y += 1; }
    setMonth(m); setYear(y);
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const monthLabel = new Intl.DateTimeFormat(intlLocale, { month: 'long', year: 'numeric' })
    .format(new Date(year, monthIdx, 1));

  const create = async (payload) => {
    const created = await api.createEvent(payload);
    setEvents(es => [...(es || []), created]);
    setComposing(false);
  };
  const update = async (payload) => {
    const updated = await api.updateEvent(editing.id, payload);
    setEvents(es => es.map(x => x.id === updated.id ? updated : x));
    setEditing(null);
  };
  const remove = async (ev) => {
    if (!window.confirm(t('calendar.confirmDelete', { title: ev.title }))) return;
    await api.deleteEvent(ev.id);
    setEvents(es => es.filter(x => x.id !== ev.id));
  };

  // Upcoming events list (right rail)
  const upcoming = useMemo(() => {
    if (!events) return [];
    const today = isoDate(new Date());
    return events
      .filter(e => e.end_date >= today)
      .slice()
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .slice(0, 8);
  }, [events]);

  // Disambiguate first names within the upcoming list (e.g. two Geoffreys
  // → "Geoffrey M." / "Geoffrey V."). Single-occurrence names stay short.
  const upcomingNameFor = useMemo(() => buildDisplayNameFor(upcoming), [upcoming]);

  return (
    <Shell reviewCount={reviewCount} wide>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-[1.75rem]">{t('calendar.title')}</h1>
          <p className="text-[15px] text-[hsl(var(--text-soft))] mt-1.5">
            {t('calendar.intro')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
            <button type="button" onClick={() => setView('month')}
              className={`px-2.5 py-1.5 inline-flex items-center gap-1.5 text-[13px] transition ${
                view === 'month'
                  ? 'bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                  : 'text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--surface-2))]'
              }`}
              aria-pressed={view === 'month'}>
              <CalendarRange className="h-3.5 w-3.5" /> {t('calendar.view.month')}
            </button>
            <button type="button" onClick={() => setView('pressure')}
              className={`px-2.5 py-1.5 inline-flex items-center gap-1.5 text-[13px] transition border-l border-border ${
                view === 'pressure'
                  ? 'bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                  : 'text-[hsl(var(--text-soft))] hover:bg-[hsl(var(--surface-2))]'
              }`}
              aria-pressed={view === 'pressure'}>
              <Gauge className="h-3.5 w-3.5" /> {t('calendar.view.pressure')}
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={goToday}>{t('common.today')}</Button>
          <div className="inline-flex items-center border border-border rounded-md overflow-hidden">
            <button type="button" onClick={goPrev}
              className="px-2 py-1.5 hover:bg-[hsl(var(--surface-2))] text-[hsl(var(--text-soft))]" aria-label={t('common.previous')}>
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="px-3 py-1.5 text-[14px] font-medium capitalize min-w-[160px] text-center">
              {monthLabel}
            </div>
            <button type="button" onClick={goNext}
              className="px-2 py-1.5 hover:bg-[hsl(var(--surface-2))] text-[hsl(var(--text-soft))]" aria-label={t('common.next')}>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {view === 'month' && !composing && !editing && (
            <Button onClick={() => setComposing(true)} className="gap-2">
              <Plus className="h-4 w-4" /> {isHR ? t('common.add') : t('calendar.addOwnAbsence')}
            </Button>
          )}
        </div>
      </header>

      {composing && (
        <EventComposer existingEmployees={employees}
          initial={typeof composing === 'object' ? composing : undefined}
          isHR={isHR}
          onSave={create} onCancel={() => setComposing(false)} />
      )}
      {editing && (
        <EventComposer existingEmployees={employees}
          initial={editing} mode="edit"
          isHR={isHR}
          onSave={update}
          onCancel={() => setEditing(null)}
          onDelete={async (ev) => { await remove(ev); setEditing(null); }} />
      )}

      {/* Department pills · only meaningful in the month view */}
      {view === 'month' && (
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {departments.map(name => (
            <button
              key={name}
              type="button"
              onClick={() => setActiveDept(name)}
              className={[
                'inline-flex items-center rounded-full border px-3 py-1.5 text-[13px] transition',
                activeDept === name
                  ? 'border-[hsl(var(--text))] bg-[hsl(var(--text))] text-[hsl(var(--bg))]'
                  : 'border-border bg-[hsl(var(--surface))] text-[hsl(var(--text-soft))] hover:text-[hsl(var(--text))] hover:border-[hsl(var(--rule-strong))]',
              ].join(' ')}
            >
              {name === ALL ? name : deptLabel(name, locale)}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3">
          {events === null
            ? <div className="bg-[hsl(var(--surface))] border border-border rounded-lg p-12 text-center text-sm text-[hsl(var(--text-soft))]">{t('common.loading')}</div>
            : view === 'pressure'
              ? <PressureGrid year={year} monthIdx={monthIdx} events={events || []} employees={employees} />
              : <MonthGrid year={year} monthIdx={monthIdx} events={filtered}
                  onEventClick={(e) => { if (canEditEvent(e)) setEditing(e); }}
                  onDayClick={(iso) => setComposing({ start_date: iso, end_date: iso })} />
          }

          {/* Legend · only in the month view (pressure has its own) */}
          {view === 'month' && (
            <div className="flex items-center gap-4 mt-3 text-[12px] text-[hsl(var(--text-muted))]">
              {KIND_KEYS.map(k => (
                <span key={k} className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: KIND_DOT[k] }} />
                  {t(`calendar.kind.${k}`)}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming */}
        <aside className="lg:col-span-1">
          <div className="bg-[hsl(var(--surface))] border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h2 className="text-[14px] font-semibold">{t('calendar.upcoming')}</h2>
            </div>
            {upcoming.length === 0
              ? <EmptyState compact title={t('calendar.upcoming.empty')} />
              : (
                <ul className="divide-y divide-[hsl(var(--rule))]">
                  {upcoming.map(e => (
                    <li key={e.id} className="px-4 py-3 text-[13px]">
                      <div className="flex items-start gap-2 min-w-0">
                        <span className="inline-block h-2 w-2 mt-1.5 rounded-full shrink-0"
                          style={{ background: KIND_DOT[e.kind] || KIND_DOT.team_event }} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{e.employee_full_name ? `${upcomingNameFor(e.employee_full_name)} · ${eventLabel(e, locale)}` : eventLabel(e, locale)}</div>
                          <div className="text-[11px] text-[hsl(var(--text-muted))] font-num">
                            {e.start_date === e.end_date
                              ? new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: 'short' }).format(new Date(e.start_date))
                              : `${new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: 'short' }).format(new Date(e.start_date))} → ${new Intl.DateTimeFormat(intlLocale, { day: '2-digit', month: 'short' }).format(new Date(e.end_date))}`}
                            {e.half_day === 'am' && <> · AM</>}
                            {e.half_day === 'pm' && <> · PM</>}
                            {e.departement && <> · {deptLabel(e.departement, locale)}</>}
                          </div>
                        </div>
                        {canEditEvent(e) && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => setEditing(e)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]" aria-label="Modifier">
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button onClick={() => remove(e)} className="text-[hsl(var(--text-muted))] hover:text-[hsl(var(--danger))]" aria-label="Supprimer">
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )
            }
          </div>

          <div className="mt-4">
            <GoogleCalendarConnect />
          </div>
        </aside>
      </div>
    </Shell>
  );
};

export default Calendar;
